import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";

// Forma que devuelve GET /api/v1/reboot/agencias (ver reboot_routes.py).
export interface AgenciaReboot {
  host: string;
  name: string;
  // Grupos del Zabbix propio. La pantalla separa por cliente con ellos
  // ("Holanda - …" / "tiLatina - …"). Opcional: una api vieja no lo manda.
  grupos?: string[];
  state: "up" | "down" | "unknown";
  down_seconds: number | null;
  via: "ssh" | "sms_telcel" | "sms_att" | "manual";
  // Comando de reinicio a distancia del equipo (independiente del estado):
  // "SMS" (Telcel), "API" (AT&T) o "Ninguno" (solo fisico).
  metodo: "SMS" | "API" | "Ninguno";
  actionable: boolean;
  reason: string;
}

interface AgenciasReboot {
  items: AgenciaReboot[];
}

interface RebootResult {
  ok: boolean;
  host: string;
  via: string;
  donor: string | null;
  detail: string;
  // Incidente en el que quedo anotado el reinicio, si habia uno abierto.
  incident_id: string | null;
}

// Un intento de reinicio, humano o automatico (ver GET /api/v1/reboot/historial).
export interface IntentoReinicio {
  ts: string;
  actor: string;
  // `true` cuando lo disparo AROC solo. Sin esto no se puede distinguir en
  // pantalla quien actuo, que es justo lo que hacia falta al encender el carril.
  automatico: boolean;
  host: string;
  via: string;
  donor: string | null;
  outcome: string;
  detail: string;
}

interface HistorialReinicios {
  items: IntentoReinicio[];
}

const REFETCH_MS = 30_000;

export function useAgenciasReboot() {
  return useQuery({
    queryKey: ["reboot-agencias"],
    queryFn: ({ signal }) => api<AgenciasReboot>("/api/v1/reboot/agencias", { signal }),
    refetchInterval: REFETCH_MS,
    placeholderData: (prev) => prev,
  });
}

export function useHistorialReinicios() {
  return useQuery({
    queryKey: ["reboot-historial"],
    queryFn: ({ signal }) =>
      api<HistorialReinicios>("/api/v1/reboot/historial?limite=20", { signal }),
    refetchInterval: REFETCH_MS,
    placeholderData: (prev) => prev,
  });
}

export function useReiniciarAgencia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (host: string) =>
      api<RebootResult>(`/api/v1/reboot/agencias/${encodeURIComponent(host)}`, {
        method: "POST",
      }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["reboot-agencias"] });
      // El intento que se acaba de hacer tiene que verse abajo sin recargar.
      void qc.invalidateQueries({ queryKey: ["reboot-historial"] });
    },
  });
}
